import {
  createSlice,
  createEntityAdapter,
  createSelector,
} from "@reduxjs/toolkit";
import {
  fetchProducts,
  fetchProductById,
  createProduct,
  deleteProduct,
  updateProduct,
  fetchProductsByCategorySlug,
} from "./thunk";

export const productsAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
});

const initialState = productsAdapter.getInitialState({
  isLoading: false,
  meta: {},
  product: null,
  category: null,
});

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all products
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, { payload }) => {
        productsAdapter.setAll(state, payload.data);
        state.meta = payload.meta || {};
        state.isLoading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isLoading = false;
      })
      // Fetch products by category slug
      .addCase(fetchProductsByCategorySlug.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProductsByCategorySlug.fulfilled, (state, { payload }) => {
        productsAdapter.addMany(state, payload.data.products);
        state.category = payload.data.category;
        state.meta = payload.meta || {};
        state.isLoading = false;
      })
      .addCase(fetchProductsByCategorySlug.rejected, (state, action) => {
        state.isLoading = false;
      })
      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.product = action.payload.data;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.isLoading = false;
      })
      // Create product
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createProduct.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        productsAdapter.addOne(state, payload.data);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
      })
      // Delete product
      .addCase(deleteProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        productsAdapter.removeOne(state, payload.data._id);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isLoading = false;
      })
      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProduct.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        productsAdapter.updateOne(state, {
          id: payload.data._id,
          changes: payload.data,
        });
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

const productsInputSelector = (state) => state.products;

const productSelectors = productsAdapter.getSelectors(productsInputSelector);

export const getProductsSelector = (state) => productSelectors.selectAll(state);

export const productDataSelector = createSelector(
  getProductsSelector,
  productsInputSelector,
  (productsData, products) => {
    return {
      data: productsData,
      ...products,
    };
  }
);

// Export reducer
export default productsSlice.reducer;
